import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Pen, Mail, BookOpen, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useCurrency } from '../context/CurrencyContext';
import { usePhoneNumber } from '../context/PhoneContext';
import { setJsonLd, setPageSeo } from '../utils/seo';
import './HowItWorksPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function HowItWorksPage() {
  // eslint-disable-next-line no-unused-vars
  const { formatPrice, getCurrencyCode } = useCurrency();
  const phone = usePhoneNumber();

  useEffect(() => {
    setPageSeo({
      title: 'How Keepsake Letters Are Made | Binding Thread',
      description: 'See how Binding Thread works in 4 steps: your loved one calls our free number, shares a memory, we craft it into a keepsake letter on archival paper, and post it to you. Three keepsake letter collections from $149 \u2014 keepsake tin and shipping included.',
      canonical: 'https://www.bindingthread.com.au/how-it-works',
      ogImage: 'https://images.unsplash.com/photo-1637580678284-da66cab7cd95?w=600&q=80'
    });
    setJsonLd('ld-howto', {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How Binding Thread Turns Phone Calls into Keepsake Letters",
      "description": "Four simple steps to preserve your family\u2019s stories forever with Binding Thread.",
      "step": [
        { "@type": "HowToStep", "position": 1, "name": "They make a simple call", "text": "Your loved one dials 1800 315 152 and speaks their memories aloud \u2014 no apps, no screens, no pressure." },
        { "@type": "HowToStep", "position": 2, "name": "We listen and preserve every word", "text": "Their spoken memories are gently transcribed and shaped into a letter that feels personal, intimate, and true to them." },
        { "@type": "HowToStep", "position": 3, "name": "You receive a keepsake letter by post", "text": "A beautifully crafted keepsake letter arrives in your mailbox \u2014 something you can hold, reread, and keep safe for generations." },
        { "@type": "HowToStep", "position": 4, "name": "A legacy grows, one letter at a time", "text": "Letter by letter, your collection builds inside the keepsake tin \u2014 quietly becoming a treasured family heirloom." }
      ]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="how-it-works-page" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="hiw-hero">
        <div className="container">
          <h1>How It Works</h1>
          <p>Four simple steps to preserve your family&apos;s stories forever.</p>
        </div>
      </section>

      {/* Steps */}
      <section className="hiw-steps">
        <div className="container">
          <div className="step-item" data-testid="step-1">
            <div className="step-number">1</div>
            <div className="step-icon">
              <Phone size={32} />
            </div>
            <div className="step-content">
              <h2>They make a simple call</h2>
              <p>
                Your loved one dials <strong>{phone.display}</strong> and speaks their memories aloud — no apps, no screens, no pressure. Just their voice, their stories, their pace.
              </p>
            </div>
            <div className="step-image">
              <img 
                src="https://images.unsplash.com/photo-1601175864100-1b8332d228d1?w=500&q=80&fm=webp" 
                alt="Smiling elderly man sharing a story over the phone"
                width="500"
                height="333"
              />
            </div>
          </div>

          <div className="step-connector"></div>

          <div className="step-item reverse" data-testid="step-2">
            <div className="step-number">2</div>
            <div className="step-icon">
              <Pen size={32} />
            </div>
            <div className="step-content">
              <h2>We listen and preserve every word</h2>
              <p>
                Their spoken memories are gently transcribed and shaped into a letter that feels personal, intimate, and true to them.
              </p>
            </div>
            <div className="step-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_719e6e75-4da9-4456-82ce-19b3fc98bea1/artifacts/zsuhcq2s_margarita_kochneva-text-4095909_1920.jpg" 
                alt="Stack of keepsake letters on parchment paper tied with twine"
                width="500"
                height="333"
              />
            </div>
          </div>

          <div className="step-connector"></div>

          <div className="step-item" data-testid="step-3">
            <div className="step-number">3</div>
            <div className="step-icon">
              <Mail size={32} />
            </div>
            <div className="step-content">
              <h2>You receive a keepsake letter by post</h2>
              <p>
                A beautifully crafted keepsake letter arrives in your mailbox &mdash; something you can hold, reread, and keep safe for generations.
              </p>
            </div>
            <div className="step-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_ab110b33-d84b-4603-b690-422196896d5d/artifacts/gzz4worw_woman%20collecting%20mail.jpg" 
                alt="Woman opening her letterbox to find a keepsake letter"
                width="500"
                height="333"
              />
            </div>
          </div>

          <div className="step-connector"></div>

          <div className="step-item reverse" data-testid="step-4">
            <div className="step-number">4</div>
            <div className="step-icon">
              <BookOpen size={32} />
            </div>
            <div className="step-content">
              <h2>A legacy grows, one letter at a time</h2>
              <p>
                Letter by letter, your collection builds inside the keepsake tin &mdash; quietly becoming a treasured family heirloom.
              </p>
            </div>
            <div className="step-image">
              <img 
                src="https://images.unsplash.com/photo-1637580678284-da66cab7cd95?w=500&q=80&fm=webp" 
                alt="Elderly hands writing memories in a journal at a wooden table"
                width="500"
                height="333"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Binding Thread */}
      <section className="hiw-why-section" data-testid="hiw-why-section">
        <div className="container">
          <div className="hiw-why-grid">
            <div className="hiw-why-content">
              <h2>Why Binding Thread?</h2>
              <p className="hiw-why-intro">
                In a world of disappearing texts and forgotten voicemails, it&apos;s too easy for the stories that matter most to slip away. You want something lasting — something you can hold, reread, and pass down.
              </p>
              <p>
                That&apos;s why Binding Thread exists. We make it simple for your loved one to share their memories, and we turn those spoken moments into <strong>beautifully crafted keepsake letters</strong>. No screens. No apps. No overwhelm. Just their voice, captured on paper that will outlive all of us.
              </p>
              <ul className="hiw-why-list">
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>No technology needed &mdash; just a phone call</span>
                </li>
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>Choose your collection: 3, 6 or 12 keepsake letters</span>
                </li>
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>Archival quality paper made to last generations</span>
                </li>
                <li>
                  <CheckCircle size={20} className="check-icon" />
                  <span>Keepsake tin and shipping included in every collection</span>
                </li>
              </ul>
              <p className="hiw-why-tagline">
                A meaningful gift for families who want to preserve their story
              </p>
              <p className="hiw-why-closing">
                You&apos;re trying to hold onto the moments that matter. We&apos;re here to help you keep them safe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Don't Wait + Early Bird CTA */}
      <section className="hiw-cta" data-testid="hiw-cta">
        <div className="container text-center">
          <h2>Don&apos;t Wait Until the Memories Are Gone</h2>
          <p className="hiw-cta-intro">
            Every family reaches a moment when they realise the stories they thought they&apos;d always remember&hellip; are fading. You can begin preserving them now, gently and simply.
          </p>

          <div className="hiw-early-bird" data-testid="hiw-collections">
            <span className="early-bird-label">Choose Your Collection</span>
            <p className="early-bird-desc">
              Three carefully crafted keepsake letter collections, capturing the stories of someone you love.
            </p>
            <div className="early-bird-price">
              <span className="price-big">From $149</span>
              <span className="price-detail">AUD / one-time</span>
            </div>
            <p className="early-bird-spots">Keepsake tin &amp; shipping included in every collection</p>
            <p className="early-bird-note">
              <strong>A Thread</strong> &middot; 3 letters &nbsp;|&nbsp; <strong>Woven</strong> &middot; 6 letters &nbsp;|&nbsp; <strong>Tapestry</strong> &middot; 12 letters
            </p>
            <Link to="/shop" className="btn btn-urgent" data-testid="hiw-cta-btn">
              See the Collections
            </Link>
          </div>
          <div className="hiw-cross-links">
            <Link to="/faq" className="inline-link">Have questions? Read our FAQs</Link>
            <span className="link-divider">&middot;</span>
            <Link to="/about" className="inline-link">Meet our founder</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HowItWorksPage;
